import * as Yup from "yup";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";

const str = (_limit?: number) => Yup.string().trim();

const req = (_limit?: number) => str().required("This field is required");

const parseMoney = (value: string): number => {
  const n = parseFloat(value.replace(/[$,\s]/g, ""));
  return isNaN(n) ? 0 : n;
};

// Mirrors payload.ts's parseNumStr so validation agrees with what's sent to the API.
const parsePercent = (value: string): number | undefined => {
  const n = parseFloat(value.replace(/[^0-9.]/g, ""));
  return isNaN(n) ? undefined : n;
};

export const step1Schema = Yup.object({
  projectName: req(CHAR_LIMIT.NAME),
  bank: str(CHAR_LIMIT.NAME),
  lendingOfficer: str(CHAR_LIMIT.NAME),
  projectDescription: str(CHAR_LIMIT.DESCRIPTION),
  ownerName: str(CHAR_LIMIT.NAME),
  gcName: str(CHAR_LIMIT.NAME),
  architectName: str(CHAR_LIMIT.NAME),
  servicesRequested: str(CHAR_LIMIT.SHORT),
  reportDate: Yup.string().required("Required"),
  invoiceNumber: req(CHAR_LIMIT.SHORT),
  invoiceAmount: req(CHAR_LIMIT.SHORT),
  preparedBy: req(CHAR_LIMIT.NAME),
  projectAddress: str(CHAR_LIMIT.ADDRESS),
  reviewDate: Yup.string(),
});

export const step2Schema = Yup.object({
  certificationOpening: str(CHAR_LIMIT.DESCRIPTION),
  certificationStandards: str(CHAR_LIMIT.DESCRIPTION),
  certificationFindings: str(CHAR_LIMIT.DESCRIPTION),
  certificationPreparedBy: str(CHAR_LIMIT.NAME),
  certificationOrganization: str(CHAR_LIMIT.NAME),
  certificationPhone: Yup.string()
    .max(CHAR_LIMIT.PHONE)
    .matches(/^[\d\s\-()+.]*$/, "Enter a valid phone number"),
  certificationEmail: Yup.string()
    .max(CHAR_LIMIT.EMAIL)
    .email("Enter a valid email address"),
});

export const step3Schema = Yup.object({
  primaryDescriptionPoints: req(CHAR_LIMIT.DESCRIPTION),
  totalSf: str(CHAR_LIMIT.SHORT),
  totalUnitsPhase1: str(CHAR_LIMIT.SHORT),
  totalUnitsFull: str(CHAR_LIMIT.SHORT),
  additionalDescription: str(CHAR_LIMIT.DESCRIPTION),
  totalBudget: str(CHAR_LIMIT.SHORT),
  hardCostBudget: str(CHAR_LIMIT.SHORT).test(
    "hard-cost-within-total",
    "Hard Cost Budget cannot be greater than Total Budget.",
    function (value) {
      const total = parseMoney(this.parent.totalBudget ?? "");
      const hard = parseMoney(value ?? "");
      if (!hard || !total) return true;
      return hard <= total;
    },
  ),
  costPerSf: str(CHAR_LIMIT.SHORT),
  mobilizationDate: req(CHAR_LIMIT.SHORT),
  estCompletion: req(CHAR_LIMIT.SHORT),
});

export const step4Schema = Yup.object({
  contractType: str(CHAR_LIMIT.SHORT),
  contractorFee: str(CHAR_LIMIT.SHORT),
  contingencyDisplay: str(CHAR_LIMIT.SHORT),
  retainage: str(CHAR_LIMIT.SHORT),
  contractNarrative: req(CHAR_LIMIT.DESCRIPTION),
});

export const step5Schema = Yup.object({
  allowancesNarrative: req(CHAR_LIMIT.DESCRIPTION),
  gcContingencyAmount: str(CHAR_LIMIT.SHORT),
  contingencyPct: str(CHAR_LIMIT.SHORT).test(
    "contingency-pct-range",
    "Contingency % must be between 0 and 100.",
    (value) => {
      if (!value) return true;
      const n = parsePercent(value);
      if (n == null) return true;
      return n >= 0 && n <= 100;
    },
  ),
  industryRange: str(CHAR_LIMIT.SHORT),
  contingencyNarrative: req(CHAR_LIMIT.DESCRIPTION),
  clarificationsNarrative: str(CHAR_LIMIT.DESCRIPTION),
});

export const step6Schema = Yup.object({
  budgetNarrative: req(CHAR_LIMIT.DESCRIPTION),
  budgetConclusion: req(CHAR_LIMIT.DESCRIPTION),
  comparisonRows: Yup.array().of(
    Yup.object({
      project: Yup.string().when("isThis", {
        is: true,
        then: (s) => s,
        otherwise: (s) => s.trim().required("Project name is required"),
      }),
    }),
  ),
});

export const step7Schema = Yup.object({
  scheduleNarrative: req(CHAR_LIMIT.DESCRIPTION),
  closingNarrative: req(CHAR_LIMIT.DESCRIPTION),
  documentsReviewed: req(CHAR_LIMIT.DESCRIPTION),
  preconRequirements: str(CHAR_LIMIT.DESCRIPTION),
  drawRequirements: str(CHAR_LIMIT.DESCRIPTION),
  closeoutRequirements: str(CHAR_LIMIT.DESCRIPTION),
  actionItems: Yup.array().of(
    Yup.object({
      party: Yup.string().trim().required("Party is required"),
    }),
  ),
});

export const STEP_SCHEMAS: Yup.ObjectSchema<object>[] = [
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
  step5Schema,
  step6Schema,
  step7Schema,
  Yup.object({}),
];
